import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ScrollbarSvg from '../scrollbar/ScrollbarSvg';

const Cell = ({
    style,
    scrollPx,
    isHighlighted = false,
    index,
    rowHeight,
    visibleRows,
    ...props
}) => {
    const upperVisibleBoundary = rowHeight * (index + 1);
    const lowerVisibleBoundary =
        upperVisibleBoundary - rowHeight * (visibleRows + 1);

    const isVisible =
        scrollPx > lowerVisibleBoundary && scrollPx < upperVisibleBoundary;

    return (
        <div
            {...props}
            style={{
                display: 'flex',
                border: '1px solid #d7d7d7',
                boxSizing: 'border-box',
                alignItems: 'center',
                justifyContent: 'space-around',
                color: '#414141',
                fontFamily: 'roboto',
                height: rowHeight,
                fontSize: 15,
                fontWeight: 500,
                position: 'relative',
                borderWidth: '0 1px 1px 0',
                padding: '0 10px',
                opacity: isVisible * 1,
                pointerEvents: isVisible ? 'auto' : 'none',
                bottom: scrollPx,
                cursor: 'default',
                backgroundColor: isHighlighted
                    ? '#DCFFFF'
                    : (index + 1) % 2
                    ? 'rgb(247,247,247)'
                    : '#ffffff',
                ...style
            }}
        />
    );
};

const Gutter = ({
    id = 'gutter',
    scrollPx,
    onScroll,
    scrollableLength,
    maxVisibleLength,
    style,
    visibleRows,
    ...props
}) => {
    const ref = useRef(null);

    const [offsetTop, setOffsetTop] = useState(-1);

    /**
     * Event handler for conventional page scroll events
     */
    const handlePageScroll = () => {
        const ot = ref?.current?.getBoundingClientRect()?.top + window.scrollY;
        // If calculation failed, this prevents setting top to undefined/null.
        if (typeof ot === 'number' && ot !== offsetTop) {
            setOffsetTop(ot);
        }
    };

    useEffect(() => {
        // Automatically calculate the gutter offset, with page scrolls
        window.addEventListener('scroll', handlePageScroll, { passive: true });
        // Run page scroll once on first render, to initalise offset
        if (offsetTop === -1) {
            handlePageScroll();
        }
        // Cleanup window listener
        return () => {
            window.removeEventListener('scroll', handlePageScroll);
        };
    }, [scrollPx, scrollableLength]);

    return (
        <div
            id={id}
            ref={ref}
            style={{
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                border: '1px solid #d7d7d7',
                borderWidth: '0 1px 1px 0',
                padding: 1,
                zIndex: 2,
                gridRow: `span ${visibleRows}`,
                ...style
            }}
        >
            <ScrollbarSvg
                {...props}
                id={`${id}Scrollbar`}
                scrollPx={scrollPx}
                onScroll={onScroll}
                gutterOffset={offsetTop}
                maxVisibleLength={maxVisibleLength - 3}
                scrollableLength={scrollableLength - 3}
            />
        </div>
    );
};

Cell.propTypes = {
    style: PropTypes.object,
    scrollPx: PropTypes.number.isRequired,
    isHighlighted: PropTypes.bool,
    index: PropTypes.number.isRequired,
    rowHeight: PropTypes.number.isRequired,
    visibleRows: PropTypes.number.isRequired
};

Gutter.propTypes = {
    id: PropTypes.string,
    scrollPx: PropTypes.number.isRequired,
    onScroll: PropTypes.func,
    scrollableLength: PropTypes.number,
    maxVisibleLength: PropTypes.number.isRequired,
    style: PropTypes.object,
    visibleRows: PropTypes.number.isRequired
};

export { Cell, Gutter };
